// The typed registry of every persisted setting. Keys and value encodings of
// settings that existed before the redesign are unchanged, so returning users
// keep their choices; invalid stored values fall back to the default.

import { LEAGUES, type League } from '../league/league';
import { COUNTRY_CODES, type CountryCode } from '../model/countries';
import { safeGet, safeSet, subscribeStorage } from './storage';

export interface SettingDef<T> {
    key: string;
    defaultValue: T;
    // Returns undefined for a value that is not valid.
    parse(raw: string): T | undefined;
    serialize(value: T): string;
}

const enumSetting = <T extends string>(key: string, values: readonly T[], defaultValue: T): SettingDef<T> => ({
    key,
    defaultValue,
    parse: raw => (values as readonly string[]).includes(raw) ? raw as T : undefined,
    serialize: value => value,
});

const boolSetting = (key: string, defaultValue: boolean): SettingDef<boolean> => ({
    key,
    defaultValue,
    parse: raw => raw === 'true' ? true : (raw === 'false' ? false : undefined),
    serialize: value => String(value),
});

const jsonSetting = <T>(key: string, defaultValue: T, isValid: (value: unknown) => value is T): SettingDef<T> => ({
    key,
    defaultValue,
    parse: raw => {
        try {
            const parsed: unknown = JSON.parse(raw);
            return isValid(parsed) ? parsed : undefined;
        } catch {
            return undefined;
        }
    },
    serialize: value => JSON.stringify(value),
});

// Sort orders are "<column>Asc" / "<column>Desc"; which columns exist depends on the table.
const sortOrderSetting = (key: string, defaultValue: string): SettingDef<string> => ({
    key,
    defaultValue,
    parse: raw => /^[a-zA-Z]+(Asc|Desc)$/.test(raw) ? raw : undefined,
    serialize: value => value,
});

export interface TeamFilterEntry {
    label: string;
    value: number;
}

const isStringArray = (value: unknown): value is string[] =>
    Array.isArray(value) && value.every(item => typeof item === 'string');

const isTeamFilter = (value: unknown): value is TeamFilterEntry[] =>
    Array.isArray(value) && value.every(item =>
        typeof item === 'object' && item !== null && typeof (item as TeamFilterEntry).value === 'number');

export const SCORE_FILTERS = ['all', 'filtered', 'none'] as const;
export type ScoreFilter = (typeof SCORE_FILTERS)[number];

// Denmark and Latvia were once stored under codes the backend does not serve.
const RENAMED_COUNTRY_CODES: Record<string, CountryCode> = { DNK: 'DEN', LVA: 'LAT' };

const countrySetting = enumSetting<CountryCode>('country', COUNTRY_CODES, 'FIN');

export const settings = {
    // Existing keys.
    country: { ...countrySetting, parse: raw => countrySetting.parse(RENAMED_COUNTRY_CODES[raw] ?? raw) } as SettingDef<CountryCode>,
    language: enumSetting('nhlcountries_language', ['fi', 'en'] as const, 'fi'),
    scoreFilter: enumSetting<ScoreFilter>('nhlcountries_scoreFilter', SCORE_FILTERS, 'all'),
    hideTeams: boolSetting('nhlcountries_hideTeams', false),
    hideTeamsList: jsonSetting<string[]>('nhlcountries_hideTeamsList', [], isStringArray),
    teamFilter: jsonSetting<TeamFilterEntry[]>('nhlcountries_teamFilter', [], isTeamFilter),
    statType: enumSetting('nhlcountries_statType', ['filtered', 'all'] as const, 'filtered'),
    skaterSortOrder: sortOrderSetting('nhlcountries_skaterSortOrder', 'pointsDesc'),
    goalieSortOrder: sortOrderSetting('nhlcountries_goalieSortOrder', 'savePctDesc'),
    // The old "Summary" tab's sort orders, now used by Tänään › Pelaajat.
    roundSkaterSortOrder: sortOrderSetting('nhlcountries_skaterSummarySortOrder', 'pointsLatestDesc'),
    roundGoalieSortOrder: sortOrderSetting('nhlcountries_goalieSummarySortOrder', 'savePctLatestDesc'),
    nationalTeamType: enumSetting('nhlcountries_nationalTeamType', ['all', 'filtered'] as const, 'all'),
    // New keys.
    theme: enumSetting('nhlcountries_theme', ['auto', 'light', 'dark', 'teletext'] as const, 'auto'),
    todayView: enumSetting('nhlcountries_todayView', ['games', 'players'] as const, 'games'),
    todayPointTracker: boolSetting('nhlcountries_todayPointTracker', false),
    seasonView: enumSetting('nhlcountries_seasonView', ['stats', 'countryPoints', 'news', 'nationalTeam'] as const, 'stats'),
    seasonPlayerType: enumSetting('nhlcountries_seasonPlayerType', ['skaters', 'goalies'] as const, 'skaters'),
    leagueView: enumSetting('nhlcountries_leagueView', ['standings', 'playoffs', 'tank'] as const, 'standings'),
    upcomingShowPlayers: boolSetting('nhlcountries_upcomingShowPlayers', true),
    // The round date on which the team hiding was last switched on automatically.
    hideTeamsDay: {
        key: 'nhlcountries_hideTeamsDay',
        defaultValue: '',
        parse: raw => raw,
        serialize: value => value,
    } as SettingDef<string>,
    conference: enumSetting('nhlcountries_conference', ['E', 'W'] as const, 'E'),
    // The league last chosen in the ⋮ menu; a fresh visit to / follows it.
    league: enumSetting<League>('nhlcountries_league', LEAGUES, 'NHL'),
};

// Settings that hold team abbreviations or ids, which differ between the
// leagues. The NHL keeps the old keys.
const ahlTeamSettings = {
    hideTeamsList: jsonSetting<string[]>('nhlcountries_ahl_hideTeamsList', [], isStringArray),
    teamFilter: jsonSetting<TeamFilterEntry[]>('nhlcountries_ahl_teamFilter', [], isTeamFilter),
};

export const leagueSettings = (league: League): typeof ahlTeamSettings => (league === 'AHL'
    ? ahlTeamSettings
    : { hideTeamsList: settings.hideTeamsList, teamFilter: settings.teamFilter });

// Parsed values are cached per raw string so that getSetting returns the same
// object until the stored value changes (required by useSyncExternalStore).
const cache = new Map<string, { raw: string | null; value: unknown }>();

export function getSetting<T>(def: SettingDef<T>): T {
    const raw = safeGet(def.key);
    const cached = cache.get(def.key);
    if (cached !== undefined && cached.raw === raw) {
        return cached.value as T;
    }
    const value = raw === null ? def.defaultValue : (def.parse(raw) ?? def.defaultValue);
    cache.set(def.key, { raw, value });
    return value;
}

export function setSetting<T>(def: SettingDef<T>, value: T): void {
    safeSet(def.key, def.serialize(value));
}

// Calls the listener when the setting may have changed (here or in another tab).
export function subscribeSetting(def: SettingDef<unknown>, listener: () => void): () => void {
    return subscribeStorage(key => {
        if (key === null || key === def.key) {
            listener();
        }
    });
}
